import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FaSpinner, FaGoogle } from "../utils/icons";
import { authService } from "../services/auth";
import { locationDestination, postAuthDestination } from "../utils/reviewerNav";
import { loadCognitoConfig, buildCognitoAuthUrl, isLocalDevMode } from "../config/cognito";
import { Alert, AlertDescription, Input, Label, Button } from "@/design-system/components/react";

const Login: React.FC = () => {
  const [credentials, setCredentials] = useState({
    userId: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // The specific protected URL the user was headed to before PrivateRoute
  // bounced them here (location.state.from). Null when they navigated to /login
  // directly — in that case we fall back to the role-based landing
  // (postAuthDestination) rather than a deep link. Preserve the full path
  // including search params (e.g. /worklist?cohort=abc, /case-viewer/<uid>).
  const fromLocation = (location.state as any)?.from;
  // `/` is the application entry point, not a user-selected destination. The
  // protected root route carries it through the login bounce so it can choose
  // the correct role home after auth; treating it as a deep link here would
  // needlessly navigate back through `/` and used to turn into `/worklist`
  // before auth, overriding annotator/reviewer landing behavior.
  const isAppEntry = fromLocation?.pathname === "/" && !fromLocation.search && !fromLocation.hash;
  const deepLink = fromLocation && !isAppEntry ? locationDestination(fromLocation) : null;

  // Google OAuth is the internal a2z-staff door (accounts are @a2zradiology.ai).
  // External partners use the email/password form above, so this is demoted to a
  // small "a2z staff" link — but the flow itself is unchanged.
  const handleGoogleLogin = async () => {
    try {
      await loadCognitoConfig();
    } catch (err: any) {
      setError(err.message || "Authentication not configured.");
      return;
    }

    // LOCAL_DEV_MODE: skip real auth, mirror the backend's LOCAL_DEV_USER_PAYLOAD
    // (token_utils.py) — same uuid, user_type=admin — so admin flows are
    // testable locally without manual cookie tampering. If you want to test
    // as a non-admin role, sign in via /set-password from a real invite.
    if (isLocalDevMode()) {
      const userId = "e4b83458-e061-70ed-95a8-6db840d6b35f";
      authService.setAuthCookies("local-dev", "local-dev", userId, true, userId);
      auth.setAuthState({
        token: "local-dev",
        refreshToken: "local-dev",
        userId,
        isAdmin: true,
        adminUserId: userId,
        userType: "admin",
      });
      navigate(deepLink ?? "/worklist");
      return;
    }

    // Save intended destination before external OAuth redirect (React state is
    // lost across the Cognito hop). OAuthCallback restores it for every role.
    if (deepLink) {
      sessionStorage.setItem("auth_redirect", deepLink);
    }

    // Production: Redirect to Cognito → Google OAuth
    window.location.href = buildCognitoAuthUrl();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const {
        token,
        refreshToken,
        agreementAccepted,
        adminUserId: loggedInAdminId,
        userId: effectiveUserId,
        isAdmin,
        userType,
        allowedCohorts,
      } = await authService.login(credentials.userId, credentials.password);

      auth.setAuthState({
        isAuthenticated: true,
        token,
        refreshToken,
        userId: effectiveUserId,
        isAdmin,
        adminUserId: loggedInAdminId,
        userType,
        agreementAccepted,
      });

      // Navigate to agreement if needed. Carry the original deep link through so
      // AgreementGate returns the user there after they accept; with no deep
      // link, AgreementGate falls back to the role-based landing.
      if (!agreementAccepted) {
        navigate("/agreement", { state: { from: fromLocation } });
      } else {
        // A genuine deep link wins for every role; PrivateRoute still enforces
        // per-role access (e.g. reviewers off /review|/case-viewer get bounced
        // back to their cohort). No deep link → role-based landing.
        navigate(deepLink ?? postAuthDestination({ userType, allowedCohorts }));
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Login failed. Please check your credentials.";

      setError(`Login failed: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-bg text-fg">
      {/* Left editorial panel — brand lockup is the hero. Desktop-first; hidden on
          narrow screens where the form panel shows a compact logo instead. */}
      <div
        className="relative hidden lg:flex flex-col flex-[1.35] overflow-hidden border-r border-border p-12"
        style={{
          background: "radial-gradient(circle at 24% 34%, rgba(67,97,238,0.12), transparent 54%)",
        }}
      >
        <div className="flex justify-between font-mono text-[9px] uppercase tracking-[0.18em] text-fg-dim">
          <span className="normal-case">a2z radiology ai</span>
          <span>Boston · MA</span>
        </div>

        <div className="my-auto flex flex-col items-center">
          <img
            src="/images/a2z-logo-dark.png"
            alt="a2z Radiology AI"
            className="h-auto w-[340px] max-w-[80%]"
          />
          <p className="mt-7 max-w-[350px] text-center text-sm leading-relaxed text-fg-muted">
            Sign in to access your worklist.
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {/* Compact lockup for narrow screens (left panel hidden) */}
          <img
            src="/images/a2z-logo-dark.png"
            alt="a2z Radiology AI"
            className="mb-8 w-44 lg:hidden"
          />

          <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-fg-muted">
            Sign in
          </div>
          <h1 className="mb-6 font-display text-xl font-semibold tracking-[-0.02em]">
            Access your worklist
          </h1>

          {error && (
            <Alert variant="destructive" className="mb-5 p-3 bg-warn-tint">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Email + password — the external / partner sign-in path (primary). */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="userId" className="text-fg-muted">
                Email
              </Label>
              <Input
                id="userId"
                type="text"
                name="userId"
                autoComplete="username"
                value={credentials.userId}
                onChange={handleInputChange}
                placeholder="you@hospital.org"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-fg-muted">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                name="password"
                autoComplete="current-password"
                value={credentials.password}
                onChange={handleInputChange}
                placeholder="••••••••"
                required
              />
            </div>

            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? (
                <>
                  <FaSpinner className="mr-2 h-4 w-4 animate-spin" />
                  <span>Signing in…</span>
                </>
              ) : (
                <span>Sign in</span>
              )}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-fg-muted">
            <Link to="/forgot-password" className="hover:text-fg transition-colors">
              Forgot password?
            </Link>
          </p>

          {/* a2z staff — Google OAuth, deliberately demoted (internal door). */}
          <div className="my-6 flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.16em] text-fg-faint">
            <span className="h-px flex-1 bg-border" />
            a2z staff
            <span className="h-px flex-1 bg-border" />
          </div>

          {/* Text is exactly "Sign in with Google" — e2e/auth.setup.ts and other
              specs authenticate via getByText("Sign in with Google"). Don't rename. */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="mx-auto flex items-center gap-2 font-mono text-[11px] tracking-[0.06em] text-fg-muted transition-colors hover:text-fg disabled:opacity-50"
          >
            <FaGoogle className="h-3 w-3" />
            Sign in with Google
          </button>

          <p className="mt-6 text-center font-mono text-[9px] leading-relaxed tracking-wider text-fg-faint">
            By signing in you accept the Terms &amp; Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
